/**
 * Представление фоновой задачи для человека.
 *
 * Карточка задачи показывала сырую метадату JSON-ом и пару «current/total» как
 * есть. У медиа-задач это давало «100 из 200» (внутренние доли шагов) и блок
 * технических ключей — пользователю нечитаемо. Здесь живут правила перевода.
 */

export interface JobProgressLike {
  current?: number
  total?: number
  percentage?: number
}

/**
 * Подпись под общей полосой прогресса.
 * `total === 100` — процентная шкала (её и так показывает процент справа),
 * поэтому дублировать «50 из 100» не нужно.
 */
export function progressCaption(progress?: JobProgressLike | null): string {
  const current = Number(progress?.current ?? 0)
  const total = Number(progress?.total ?? 0)
  if (!Number.isFinite(total) || total <= 0) {
    return current > 0 ? `Обработано: ${current}` : 'Выполняется…'
  }
  if (total === 100) return 'Выполняется…'
  return `${current} из ${total}`
}

export interface JobOutcomeLike {
  status?: string | null
  error?: string | null
  steps?: Array<{ status?: string | null }> | null
}

export type JobOutcome = 'running' | 'success' | 'warning' | 'failed'

/**
 * Итог задачи для карточки.
 *
 * `warning` — задача формально завершилась, но что-то не доехало. Так уходит
 * прогон, в котором визуалы отрендерены, а на FTP не выгружены: рендер терять
 * из-за недоступного FTP неправильно, поэтому прогон остаётся completed, шаг
 * выгрузки помечается failed, а причина кладётся в error. Зелёная галочка на
 * такой задаче означала бы, что карточка уедет с картинками — а она уедет
 * с локальными ссылками, которые маркетплейс не скачает.
 */
export function jobOutcome(job: JobOutcomeLike | null | undefined): JobOutcome {
  if (!job) return 'running'
  if (job.status === 'failed') return 'failed'
  if (job.status !== 'completed') return 'running'
  const stepFailed = (job.steps ?? []).some((step) => step?.status === 'failed')
  return job.error || stepFailed ? 'warning' : 'success'
}

/** Технические ключи метадаты — их пользователю показывать незачем. */
const TECHNICAL_KEYS = new Set([
  'art', 'category_prefix', 'design_type', 'jobId', 'job_id', 'run_id', 'runId',
  'userId', 'user_id', 'designCodes', 'designs', 'templatesCount', 'errors',
  'pipeline_runs', 'pipeline_errors', 'pipeline_continues', 'pipeline_launch_status',
  'outcomeUnknown', 'checkTasksBeforeNewAttempt', 'submissionLocked',
])

/** Известные технические ключи, у которых есть человеческая подпись. */
const KEY_LABELS: Record<string, string> = {
  created: 'Создано',
  pipeline_mode: 'Сценарий',
  publication_mode: 'Карточки',
  targets_count: 'Кабинетов',
  targetsCount: 'Кабинетов',
  templatesCount: 'Файлов',
  dry_run: 'Сухой прогон',
  cards_merge_enabled: 'Склейки кабинета',
  applied: 'Применено',
  pending: 'Ожидают подтверждения',
  skipped: 'Пропущено',
  failed: 'Ошибок',
}

const VALUE_LABELS: Record<string, string> = {
  designs_only: 'Только макеты',
  designs_and_products: 'Макеты и товары',
  full_pipeline: 'Полный пайплайн',
  draft: 'Черновики',
  publish: 'Публикация',
}

function formatValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'boolean') return value ? 'да' : 'нет'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return VALUE_LABELS[value] ?? value
  if (Array.isArray(value)) {
    return value.length > 0 ? `${value.length}` : null
  }
  return null
}

/**
 * Пары «подпись → значение» для карточки задачи.
 *
 * Показываем то, что уже названо по-русски самим сервисом (ключ на кириллице),
 * плюс известные технические ключи с переводом. Всё остальное остаётся в
 * блоке технических деталей.
 */
export function readableJobFacts(metadata: Record<string, unknown> | null | undefined): Array<[string, string]> {
  if (!metadata) return []
  const facts: Array<[string, string]> = []
  for (const [key, raw] of Object.entries(metadata)) {
    const cyrillicLabel = /[А-Яа-яЁё]/.test(key)
    if (!cyrillicLabel && (TECHNICAL_KEYS.has(key) || !KEY_LABELS[key])) continue
    const value = formatValue(raw)
    if (value === null) continue
    facts.push([cyrillicLabel ? key : KEY_LABELS[key], value])
  }
  return facts
}
